import React from "react";
import { PencilFill } from "../icons";
import { useTranslation } from "react-i18next";
import { usePendingDelay } from "../hooks/usePendingDelay";
import { PendingContent } from "../shared/PendingDots";
import "../../styles/person-name-editor.scss";

interface PersonNameEditorProps {
  personIndex: number;
  personName: string;
  isEditing: boolean;
  editingValue: string;
  isSaving?: boolean;
  saveError?: string;
  isCurrentPerson: boolean;
  onEdit: (index: number) => void;
  onSave: (index: number) => void | Promise<void>;
  onRemove?: (index: number) => void;
  onSelect?: (index: number) => void;
  onEditValueChange: (value: string) => void;
}

const PersonNameEditor: React.FC<PersonNameEditorProps> = ({
  personIndex,
  personName,
  isEditing,
  editingValue,
  isSaving,
  saveError,
  isCurrentPerson,
  onEdit,
  onSave,
  onRemove,
  onSelect,
  onEditValueChange,
}) => {
  const { t } = useTranslation();
  const showDots = usePendingDelay(isSaving ?? false);

  if (isEditing) {
    return (
      <div className="person-name-editor person-name-editor--editing gcw:flex-wrap">
        <div className="person-name-editor__edit-container">
          <div className="person-name-editor__input-wrapper">
            <input
              type="text"
              value={editingValue}
              disabled={isSaving}
              aria-busy={isSaving || undefined}
              onChange={(e) => onEditValueChange(e.target.value)}
              className="person-name-editor__input"
              aria-label={t("edit_person_name", "Edit person name")}
            />
            <button
              type="button"
              className="person-name-editor__save-btn"
              onClick={() => onSave(personIndex)}
              disabled={isSaving}
              aria-busy={isSaving || undefined}
            >
              <PendingContent showDots={showDots}>{t("save", "Save")}</PendingContent>
            </button>
          </div>
          {onRemove && (
            <button
              type="button"
              className="person-name-editor__delete-btn"
              onClick={() => onRemove(personIndex)}
              disabled={isSaving}
              aria-busy={isSaving || undefined}
            >
              <PendingContent showDots={showDots}>{t("delete", "Delete")}</PendingContent>
            </button>
          )}
        </div>
        {saveError && (
          <div
            className="gcw:rounded-md gcw:border gcw:border-error gcw:bg-error/10 gcw:text-error widget-error-panel
              gcw:p-2 gcw:text-sm gcw:mt-2 gcw:basis-full gcw:w-full"
            role="alert"
          >
            {saveError}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`person-name-editor ${isCurrentPerson ? "person-name-editor--current" : ""}`}>
      <div className="person-name-editor__name-section">
        <button
          type="button"
          className="person-name-editor__name-btn"
          onClick={() => onSelect?.(personIndex)}
          aria-label={t("select_person", "Select {{name}}", { name: personName })}
        >
          {personName}
        </button>
        <button
          type="button"
          className="person-name-editor__edit-btn"
          onClick={() => onEdit(personIndex)}
          aria-label={t("edit_person", "Edit {{name}}", { name: personName })}
        >
          <PencilFill size={16} />
        </button>
      </div>
      <div className="person-name-editor__divider" />
      <div className="person-name-editor__checkbox-section">
        <input
          type="checkbox"
          checked={isCurrentPerson}
          onChange={() => onSelect?.(personIndex)}
          className="person-name-editor__checkbox"
          aria-label={t("ordering_for_person", "Ordering for {{name}}", { name: personName })}
        />
      </div>
    </div>
  );
};

export default PersonNameEditor;
